import { useEffect, useRef, useState } from 'react';

/**
 * Lightweight scroll reveal using IntersectionObserver.
 * Adds an `isVisible` boolean you can wire to className.
 */
export default function useRevealOnScroll({ rootMargin = '0px 0px -10% 0px', once = true } = {}) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Respect reduced motion: show immediately.
    const prefersReduced =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReduced) {
      setIsVisible(true);
      return;
    }

    if (typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }

    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setIsVisible(true);
            if (once) obs.disconnect();
            break;
          }
        }
      },
      { root: null, threshold: 0.12, rootMargin }
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, [rootMargin, once]);

  return { ref, isVisible };
}
